import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { RotateCw, RotateCcw, Save, X, Trash2, Image as ImageIcon, ZoomIn, ZoomOut } from 'lucide-react';

const ImageEditorModal = ({ 
    image, 
    onSave, 
    onCancel, 
    onDelete, 
    onChangeImage,
    initialTransform = {},
    aspect = 2 // Default to 2:1 for PDW images if not specified
}) => {
    const [crop, setCrop] = useState(initialTransform.crop || { x: 0, y: 0 });
    const [zoom, setZoom] = useState(initialTransform.zoom || 1);
    const [rotation, setRotation] = useState(initialTransform.rotation || 0);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(initialTransform.cropAreaPixels || null);

    const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const handleRotation = (dir) => {
        let newRot = rotation + (dir === 'cw' ? 90 : -90);
        // Normalize rotation to 0-270
        if (newRot >= 360) newRot = 0;
        if (newRot < 0) newRot = 270;
        setRotation(newRot);
    };

    const handleSave = () => {
        onSave({
            crop,
            zoom,
            rotation,
            cropAreaPixels: croppedAreaPixels,
            is_deleted: false
        });
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 md:p-8 animate-in fade-in duration-200">
            <div className="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
                
                {/* Header */}
                <div className="p-4 border-b flex items-center justify-between bg-gray-50">
                    <div className="flex items-center gap-2">
                        <div className="p-2 bg-plantlix-100 rounded-lg text-plantlix-700">
                            <ImageIcon size={20} />
                        </div>
                        <h3 className="font-semibold text-gray-800">Bild bearbeiten</h3>
                    </div>
                    <button 
                        onClick={onCancel}
                        className="p-2 hover:bg-gray-200 rounded-full transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Cropper Area */}
                <div className="relative flex-grow bg-gray-900 min-h-[400px]">
                    <Cropper
                        image={image}
                        crop={crop}
                        zoom={zoom}
                        rotation={rotation}
                        aspect={aspect}
                        onCropChange={setCrop}
                        onCropComplete={onCropComplete}
                        onZoomChange={setZoom}
                        onRotationChange={setRotation}
                        objectFit="contain"
                    />
                </div>

                {/* Controls */}
                <div className="p-6 bg-white border-t space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {/* Zoom Control */}
                        <div className="space-y-3">
                            <div className="flex justify-between items-center text-sm font-medium text-gray-600">
                                <span className="flex items-center gap-2"><ZoomIn size={14}/> Zoom</span>
                                <span>{Math.round(zoom * 100)}%</span>
                            </div>
                            <input
                                type="range"
                                value={zoom}
                                min={1}
                                max={3}
                                step={0.1}
                                aria-labelledby="Zoom"
                                onChange={(e) => setZoom(Number(e.target.value))}
                                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-plantlix-600"
                            />
                        </div>

                        {/* Rotation Control */}
                        <div className="space-y-3">
                            <label className="text-sm font-medium text-gray-600 block">Rotation (90° Schritte)</label>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => handleRotation('ccw')}
                                    className="flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors border border-gray-200"
                                >
                                    <RotateCcw size={18} /> -90°
                                </button>
                                <button
                                    onClick={() => handleRotation('cw')}
                                    className="flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors border border-gray-200"
                                >
                                    <RotateCw size={18} /> +90°
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Footer Actions */}
                    <div className="flex flex-wrap items-center justify-between gap-4 pt-4 border-t">
                        <div className="flex gap-3">
                            <button
                                onClick={onDelete}
                                className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-xl transition-colors font-medium border border-transparent hover:border-red-200"
                            >
                                <Trash2 size={18} /> Löschen
                            </button>
                            <button
                                onClick={onChangeImage}
                                className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-xl transition-colors font-medium border border-gray-200"
                            >
                                <ImageIcon size={18} /> Bild ändern
                            </button>
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={onCancel}
                                className="px-6 py-2 text-gray-600 hover:bg-gray-100 rounded-xl transition-colors font-medium"
                            >
                                Abbrechen
                            </button>
                            <button
                                onClick={handleSave}
                                className="flex items-center gap-2 px-8 py-2 bg-plantlix-600 text-white rounded-xl hover:bg-plantlix-700 shadow-lg shadow-plantlix-200 transition-all font-semibold active:scale-95"
                            >
                                <Save size={18} /> Speichern
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ImageEditorModal;
